import React, { useState } from 'react';
import { Shield, User, Heart, Lock, ArrowRight, AlertTriangle } from 'lucide-react';

import { Button } from '../ui';
import { WebIDData } from '../../services/webids';

/**
 * @component WebIDActivationPrompt
 * @description Componente que muestra la opción de activar un perfil de emergencia
 * Se muestra cuando un usuario visita un WebID habilitado pero no activado
 */

interface WebIDActivationPromptProps {
  /** Datos del WebID */
  webId: WebIDData;
  /** Función llamada cuando el usuario decide activar */
  onActivate: () => void;
  /** Estado de carga */
  loading?: boolean;
  /** Error a mostrar */
  error?: string | null;
}

const WebIDActivationPrompt: React.FC<WebIDActivationPromptProps> = ({
  webId,
  onActivate,
  loading = false,
  error = null,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-blue-600 px-6 py-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Perfil de Emergencia
          </h1>
          <p className="text-primary-100">
            Activa tu perfil para acceso rápido en emergencias
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* WebID Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <Heart className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">ID del Perfil</p>
                <p className="text-sm text-gray-600 font-mono">{webId.id}</p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              ¿Por qué activar tu perfil?
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="h-3 w-3 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Acceso Rápido</p>
                  <p className="text-gray-600 text-xs">
                    Servicios de emergencia pueden acceder a tu información médica al instante
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="h-3 w-3 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Control Total</p>
                  <p className="text-gray-600 text-xs">
                    Tú decides qué información compartir y con quién
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Lock className="h-3 w-3 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">Privacidad Protegida</p>
                  <p className="text-gray-600 text-xs">
                    Cumplimos con todas las regulaciones chilenas de protección de datos
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 text-sm mb-1">
                  Protección de Datos Personales
                </p>
                <p className="text-blue-800 text-xs leading-relaxed">
                  Al activar tu perfil, aceptas nuestros términos de uso y política de privacidad. 
                  Cumplimos con la Ley N° 19.628 sobre Protección de Datos de Carácter Personal de Chile.
                </p>
                <button
                  type="button"
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-blue-600 text-xs underline mt-2 hover:text-blue-800"
                >
                  {showDetails ? 'Ocultar detalles' : 'Ver detalles de privacidad'}
                </button>
              </div>
            </div>
            
            {showDetails && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="text-xs text-blue-800 space-y-2">
                  <p><strong>Derechos ARCO:</strong> Tienes derecho a Acceder, Rectificar, Cancelar y Oponerte al tratamiento de tus datos personales.</p>
                  <p><strong>Finalidad:</strong> Los datos se utilizan exclusivamente para proporcionar información médica en situaciones de emergencia.</p>
                  <p><strong>Responsable:</strong> NFCores es responsable del tratamiento de tus datos personales.</p>
                  <p><strong>Contacto:</strong> Para ejercer tus derechos, contacta a privacy@nfcores.cl</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <Button
            onClick={onActivate}
            disabled={loading}
            className="w-full bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02]"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Activando...
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Shield className="h-5 w-5" />
                Activar Perfil de Emergencia
                <ArrowRight className="h-4 w-4" />
              </div>
            )}
          </Button>

          {/* Help Text */}
          <p className="text-center text-xs text-gray-500 mt-4">
            La activación requiere una cuenta de Google para verificar tu identidad
          </p>
        </div>
      </div>
    </div>
  );
};

export default WebIDActivationPrompt;

/**
 * @component WebIDDisabledMessage
 * @description Mensaje que se muestra cuando un WebID está deshabilitado
 */

interface WebIDDisabledMessageProps {
  webId: string;
}

export const WebIDDisabledMessage: React.FC<WebIDDisabledMessageProps> = ({ webId }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-gray-400" />
        </div>
        
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Perfil No Disponible
        </h1>
        
        <p className="text-gray-600 mb-4">
          Este perfil de emergencia no está disponible actualmente.
        </p>
        
        <div className="p-3 bg-gray-50 rounded-lg mb-4">
          <p className="text-sm text-gray-500 font-mono">{webId}</p>
        </div>
        
        <p className="text-xs text-gray-500">
          Si crees que esto es un error, contacta al administrador del sistema.
        </p>
      </div>
    </div>
  );
};
